import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type { Outfit } from '../../shared/outfits';
import type { PlannerEntry, Trip } from '../../shared/planner';
import { OCCASIONS, type Occasion, type WardrobeItem } from '../../shared/wardrobe';
import { listWardrobeItems } from '../lib/items';
import { listOutfits } from '../lib/outfits';
import {
  createPlannerEntry,
  createTrip,
  deletePlannerEntry,
  deleteTrip,
  listPlannerEntries,
  listTrips,
  setPlannerStatus,
  setTripItemPacked,
} from '../lib/planner';

function toLocalIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

const isoToday = () => toLocalIsoDate(new Date());

function formatDate(value: string, options?: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    ...options,
  }).format(new Date(`${value}T12:00:00`));
}

function Calendar({
  month,
  selectedDate,
  entries,
  onMonthChange,
  onSelect,
}: {
  month: Date;
  selectedDate: string;
  entries: PlannerEntry[];
  onMonthChange: (offset: number) => void;
  onSelect: (date: string) => void;
}) {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const leading = (new Date(year, monthIndex, 1).getDay() + 6) % 7;
  const count = new Date(year, monthIndex + 1, 0).getDate();
  const dates = Array.from({ length: leading + count }, (_, index) =>
    index < leading ? null : new Date(year, monthIndex, index - leading + 1),
  );
  const monthLabel = new Intl.DateTimeFormat(undefined, {
    month: 'long',
    year: 'numeric',
  }).format(month);
  const plannedDates = new Map<string, PlannerEntry['status'][]>();
  for (const entry of entries) {
    plannedDates.set(entry.plannedFor, [
      ...(plannedDates.get(entry.plannedFor) ?? []),
      entry.status,
    ]);
  }

  return (
    <section className="calendar-card planner-calendar">
      <div className="calendar-heading">
        <button aria-label="Previous month" type="button" onClick={() => onMonthChange(-1)}>
          ‹
        </button>
        <h2>{monthLabel}</h2>
        <button aria-label="Next month" type="button" onClick={() => onMonthChange(1)}>
          ›
        </button>
      </div>
      <div className="calendar-weekdays">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
          <span key={`${day}-${index}`}>{day}</span>
        ))}
      </div>
      <div className="calendar-grid">
        {dates.map((date, index) => {
          if (!date) return <span aria-hidden="true" key={`empty-${index}`} />;
          const value = toLocalIsoDate(date);
          const states = plannedDates.get(value) ?? [];
          return (
            <button
              aria-label={formatDate(value, { year: 'numeric' })}
              aria-pressed={selectedDate === value}
              className={[
                value === isoToday() ? 'is-today' : '',
                selectedDate === value ? 'is-selected' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              key={value}
              type="button"
              onClick={() => onSelect(value)}
            >
              {date.getDate()}
              {states.length ? (
                <span className={`calendar-dot${states.includes('worn') ? ' is-worn' : ''}`} />
              ) : null}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function PlanForm({
  date,
  outfits,
  wardrobe,
  onCreated,
}: {
  date: string;
  outfits: Outfit[];
  wardrobe: WardrobeItem[];
  onCreated: (entry: PlannerEntry) => void;
}) {
  const [outfitId, setOutfitId] = useState('');
  const [garmentId, setGarmentId] = useState('');
  const [occasion, setOccasion] = useState<Occasion>('Everyday');
  const [status, setStatus] = useState<PlannerEntry['status']>('planned');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    try {
      const entry = await createPlannerEntry({
        plannedFor: date,
        status,
        ...(outfitId ? { outfitId } : {}),
        ...(!outfitId && garmentId ? { garmentIds: [garmentId] } : {}),
        occasion,
        ...(notes.trim() ? { notes } : {}),
      });
      onCreated(entry);
      setOutfitId('');
      setGarmentId('');
      setNotes('');
      setMessage('Added to your agenda.');
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'The plan could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="planner-form" onSubmit={(event) => void submit(event)}>
      <div className="planner-section-heading">
        <div>
          <p className="eyebrow">{formatDate(date, { weekday: 'long' })}</p>
          <h2>Plan this day</h2>
        </div>
      </div>
      <label className="field">
        <span>Saved outfit</span>
        <select value={outfitId} onChange={(event) => setOutfitId(event.target.value)}>
          <option value="">Choose an outfit</option>
          {outfits.map((outfit) => (
            <option key={outfit.id} value={outfit.id}>
              {outfit.name}
            </option>
          ))}
        </select>
      </label>
      {!outfitId ? (
        <label className="field">
          <span>Or choose one piece</span>
          <select value={garmentId} onChange={(event) => setGarmentId(event.target.value)}>
            <option value="">Choose a garment</option>
            {wardrobe.map((garment) => (
              <option key={garment.id} value={garment.id}>
                {garment.name} · {garment.category}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div className="planner-form-grid">
        <label className="field">
          <span>Occasion</span>
          <select
            value={occasion}
            onChange={(event) => setOccasion(event.target.value as Occasion)}
          >
            {OCCASIONS.map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>State</span>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as PlannerEntry['status'])}
          >
            <option value="planned">Planned</option>
            <option value="worn">Already worn</option>
          </select>
        </label>
      </div>
      <label className="field">
        <span>Notes</span>
        <textarea
          placeholder="Dinner, work presentation, travel day…"
          rows={3}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>
      {message ? <p className="planner-message">{message}</p> : null}
      <button className="button button--primary" disabled={busy} type="submit">
        {busy ? 'Saving…' : 'Add to agenda'}
      </button>
    </form>
  );
}

function Agenda({
  entries,
  selectedDate,
  onChanged,
}: {
  entries: PlannerEntry[];
  selectedDate: string;
  onChanged: () => Promise<void>;
}) {
  const selected = entries.filter((entry) => entry.plannedFor === selectedDate);
  return (
    <section className="agenda-card">
      <div className="planner-section-heading">
        <div>
          <p className="eyebrow">Agenda</p>
          <h2>{formatDate(selectedDate, { weekday: 'long' })}</h2>
        </div>
        <span>{selected.length} looks</span>
      </div>
      {selected.length ? (
        <div className="agenda-list">
          {selected.map((entry) => (
            <article className="agenda-entry" key={entry.id}>
              <span className="agenda-thumbnails">
                {entry.garments.slice(0, 3).map((garment) => (
                  <img alt="" key={garment.id} src={garment.photoUrl} />
                ))}
              </span>
              <div>
                <span className={`status-pill status-pill--${entry.status}`}>
                  {entry.status === 'worn' ? 'Worn' : 'Planned'}
                </span>
                <h3>{entry.outfit?.name ?? entry.garments.map((item) => item.name).join(' + ')}</h3>
                <p>
                  {entry.occasion}
                  {entry.notes ? ` · ${entry.notes}` : ''}
                </p>
              </div>
              <div className="agenda-actions">
                <button
                  className="text-button"
                  type="button"
                  onClick={() =>
                    void setPlannerStatus(
                      entry.id,
                      entry.status === 'worn' ? 'planned' : 'worn',
                    ).then(onChanged)
                  }
                >
                  {entry.status === 'worn' ? 'Mark planned' : 'Mark worn'}
                </button>
                <button
                  className="text-button text-button--danger"
                  type="button"
                  onClick={() => void deletePlannerEntry(entry.id).then(onChanged)}
                >
                  Remove
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <p className="planner-empty-copy">Nothing planned yet. Add a saved outfit for this day.</p>
      )}
    </section>
  );
}

function TripPlanner({
  outfits,
  wardrobe,
  trips,
  onChanged,
}: {
  outfits: Outfit[];
  wardrobe: WardrobeItem[];
  trips: Trip[];
  onChanged: () => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [destination, setDestination] = useState('');
  const [startDate, setStartDate] = useState(isoToday());
  const [endDate, setEndDate] = useState(isoToday());
  const [activities, setActivities] = useState('');
  const [outfitIds, setOutfitIds] = useState<string[]>([]);
  const [garmentIds, setGarmentIds] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  function toggle(id: string, current: string[], setter: (value: string[]) => void) {
    setter(current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    try {
      await createTrip({
        name,
        destination,
        startDate,
        endDate,
        activities: activities
          .split(',')
          .map((activity) => activity.trim())
          .filter(Boolean),
        outfitIds,
        garmentIds,
      });
      setName('');
      setDestination('');
      setActivities('');
      setOutfitIds([]);
      setGarmentIds([]);
      await onChanged();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'The trip could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="trip-layout">
      <form className="planner-form trip-form" onSubmit={(event) => void submit(event)}>
        <div className="planner-section-heading">
          <div>
            <p className="eyebrow">Trip planner</p>
            <h2>Build a packing list</h2>
          </div>
        </div>
        <div className="planner-form-grid">
          <label className="field">
            <span>Trip name</span>
            <input required value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label className="field">
            <span>Destination</span>
            <input
              required
              value={destination}
              onChange={(event) => setDestination(event.target.value)}
            />
          </label>
          <label className="field">
            <span>Start</span>
            <input
              required
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </label>
          <label className="field">
            <span>End</span>
            <input
              required
              min={startDate}
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </label>
        </div>
        <label className="field">
          <span>
            Activities <small>separate with commas</small>
          </span>
          <input
            placeholder="Dinner, sightseeing, wedding"
            value={activities}
            onChange={(event) => setActivities(event.target.value)}
          />
        </label>
        <fieldset className="trip-choices">
          <legend>Outfits</legend>
          <div>
            {outfits.map((outfit) => (
              <button
                aria-pressed={outfitIds.includes(outfit.id)}
                className={outfitIds.includes(outfit.id) ? 'is-selected' : ''}
                key={outfit.id}
                type="button"
                onClick={() => toggle(outfit.id, outfitIds, setOutfitIds)}
              >
                {outfit.name}
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset className="trip-choices">
          <legend>Extra pieces</legend>
          <div>
            {wardrobe.map((garment) => (
              <button
                aria-pressed={garmentIds.includes(garment.id)}
                className={garmentIds.includes(garment.id) ? 'is-selected' : ''}
                key={garment.id}
                type="button"
                onClick={() => toggle(garment.id, garmentIds, setGarmentIds)}
              >
                {garment.name}
              </button>
            ))}
          </div>
        </fieldset>
        <p className="planner-helper">
          Pieces shared by several outfits are automatically listed only once.
        </p>
        {message ? <p className="planner-message">{message}</p> : null}
        <button className="button button--primary" disabled={busy} type="submit">
          {busy ? 'Creating…' : 'Create trip'}
        </button>
      </form>

      <section className="trip-list">
        {trips.length ? (
          trips.map((trip) => {
            const packed = trip.items.filter((item) => item.packed).length;
            return (
              <article className="trip-card" key={trip.id}>
                <div className="planner-section-heading">
                  <div>
                    <p className="eyebrow">{trip.destination}</p>
                    <h2>{trip.name}</h2>
                    <p>
                      {formatDate(trip.startDate)} – {formatDate(trip.endDate)}
                    </p>
                  </div>
                  <strong>
                    {packed}/{trip.items.length}
                  </strong>
                </div>
                {trip.activities.length ? (
                  <div className="activity-list">
                    {trip.activities.map((activity) => (
                      <span key={activity}>{activity}</span>
                    ))}
                  </div>
                ) : null}
                <div className="packing-list">
                  {trip.items.map((item) => (
                    <label key={item.garment.id}>
                      <input
                        checked={item.packed}
                        type="checkbox"
                        onChange={(event) =>
                          void setTripItemPacked(
                            trip.id,
                            item.garment.id,
                            event.target.checked,
                          ).then(onChanged)
                        }
                      />
                      <img alt="" src={item.garment.photoUrl} />
                      <span>
                        <strong>{item.garment.name}</strong>
                        <small>{item.garment.category}</small>
                      </span>
                    </label>
                  ))}
                </div>
                <button
                  className="text-button text-button--danger"
                  type="button"
                  onClick={() => void deleteTrip(trip.id).then(onChanged)}
                >
                  Delete trip
                </button>
              </article>
            );
          })
        ) : (
          <section className="agenda-card">
            <h2>Your first trip can start here</h2>
            <p className="planner-empty-copy">
              Select saved outfits and the packing list will collect every unique piece.
            </p>
          </section>
        )}
      </section>
    </div>
  );
}

export function PlannerPage() {
  const [mode, setMode] = useState<'calendar' | 'trips'>('calendar');
  const [month, setMonth] = useState(() => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState(isoToday());
  const [entries, setEntries] = useState<PlannerEntry[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [outfits, setOutfits] = useState<Outfit[]>([]);
  const [wardrobe, setWardrobe] = useState<WardrobeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  async function refresh() {
    const [nextEntries, nextTrips] = await Promise.all([listPlannerEntries(), listTrips()]);
    setEntries(nextEntries);
    setTrips(nextTrips);
  }

  useEffect(() => {
    void Promise.all([listPlannerEntries(), listTrips(), listOutfits(), listWardrobeItems()])
      .then(([nextEntries, nextTrips, nextOutfits, nextWardrobe]) => {
        setEntries(nextEntries);
        setTrips(nextTrips);
        setOutfits(nextOutfits);
        setWardrobe(nextWardrobe);
      })
      .catch((caught: unknown) =>
        setMessage(caught instanceof Error ? caught.message : 'Your planner could not load.'),
      )
      .finally(() => setLoading(false));
  }, []);

  const upcoming = useMemo(
    () => entries.filter((entry) => entry.plannedFor >= isoToday() && entry.status === 'planned'),
    [entries],
  );

  if (loading) {
    return (
      <div className="page">
        <p>Opening your planner…</p>
      </div>
    );
  }

  return (
    <div className="page planner-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Outfit calendar</p>
          <h1>Planner</h1>
          <p>Plan ahead, remember what you actually wore, and pack without duplicates.</p>
        </div>
        <div className="planner-tabs">
          <button
            aria-pressed={mode === 'calendar'}
            className={mode === 'calendar' ? 'is-active' : ''}
            type="button"
            onClick={() => setMode('calendar')}
          >
            Calendar
          </button>
          <button
            aria-pressed={mode === 'trips'}
            className={mode === 'trips' ? 'is-active' : ''}
            type="button"
            onClick={() => setMode('trips')}
          >
            Trips
          </button>
        </div>
      </header>
      {message ? <p className="planner-message">{message}</p> : null}
      {mode === 'calendar' ? (
        <>
          <div className="planner-overview">
            <Calendar
              entries={entries}
              month={month}
              selectedDate={selectedDate}
              onMonthChange={(offset) =>
                setMonth(
                  (current) => new Date(current.getFullYear(), current.getMonth() + offset, 1),
                )
              }
              onSelect={setSelectedDate}
            />
            <PlanForm
              date={selectedDate}
              outfits={outfits}
              wardrobe={wardrobe}
              onCreated={(entry) => setEntries((current) => [...current, entry])}
            />
          </div>
          <Agenda entries={entries} selectedDate={selectedDate} onChanged={refresh} />
          {upcoming.length ? (
            <p className="planner-helper">
              {upcoming.length} planned look{upcoming.length === 1 ? '' : 's'} coming up.
            </p>
          ) : null}
        </>
      ) : (
        <TripPlanner outfits={outfits} trips={trips} wardrobe={wardrobe} onChanged={refresh} />
      )}
    </div>
  );
}
